import { casOpsApi, CollectionFilteredModel, QueryTag, TagList } from '../api';
import { ModelWithId } from './commonModels';
import { ObjectType } from './commonModels';
import { getUrlParams } from './helpers';
import { FiltersState } from '../slices/filtersSlice';
import { PositionModel } from '@cas/types/assets';

export enum AssetDeviceType {
  Unknown = 'Unknown',
  X1 = 'X1',
  X9 = 'X9',
  IVU = 'IVU',
  IVUPlus = 'IVUPlus',
  PPU = 'PPU',
  IVUPlusGen2 = 'IVUPlusGen2',
}

export enum DisplayStatus {
  NotSilent = 'NotSilent',
  Silent = 'Silent',
}

export enum AssetFileDeviceStatus {
  Unknown = 'Unknown',
  Downloading = 'Downloading',
  Failed = 'Failed',
  Executing = 'Executing',
  Ready = 'Ready',
  MismatchWithConfig = 'MismatchWithConfig',
  MissingFromDevice = 'MissingFromDevice',
  MissingFromConfig = 'MissingFromConfig',
}

export interface AssetFileDeviceModel {
  id: number;
  name: string;
  ver: string;
  rep: string | null; // report time
  at: string; // activation time
  app: boolean | null; // applied
  stat: AssetFileDeviceStatus;
}

export interface AssetComponentModel {
  type: string;
  hwRev?: string;
  serNo?: string;
  bootVer?: string;
  firmVer?: string;
  compId?: string;
}

export interface AssetFaultModel {
  lvl: number; //the fault level (1-3)
  cat: number; //the ID of the fault category
  flt: number; //the ID of the fault
  ts: string; //timestamp of the time the fault was raised
}

export interface AssetFaultDisplayModel extends AssetFaultModel {
  dur: number; // seconds
}

export interface RegisteredAssetBasicModel extends ModelWithId {
  did: string; // Device ID (IMEI)
  n: string; // CAS Server Name - Max 8 chars with regex "^[a-zA-Z0-9-_\\.\\s]+$". WARN user if > 6 chars
  hw: AssetDeviceType;
  cas: string; // CAS Software version
  obj: ObjectType;
  cfg: number | null; // Configuration group ID. Null if AssetDeviceType === PPU
  ags: number[]; // Asset group IDs
  ts: string; // Last configuration change time (in ISO format, same as Portal)
  stN: boolean; // Status name is good (no mismatch)
  stC: boolean; // Software Status configuration is up to date (matches configuration group)
  stH: boolean; // Status health (good = no faults)
  totG: number; // total number of geofences associated with this asset (via asset groups inclusions)
}

export interface RegisteredAssetDetailModel extends RegisteredAssetBasicModel {
  osVer: string;
  geoVer: number; // Device geofence version
  geoTs?: string; // Device geo timestamp "2023-01-01T00:00:00Z"
  siteGeoVer: number; // Site geo version
  siteGeoTs?: string; // Site geo timestamp
  components: AssetComponentModel[];
  files: AssetFileDeviceModel[];
  flts: AssetFaultModel[];
  cfgSts: AssetFileDeviceModel[];
  dn: string; // Name in the device
  tsRp?: string; // Time of last good report (GPS Time)
  tsRx?: string; // Last connected time. CAS Web (server) time of last message received.
  tsCfg: string;
  posG?: PositionModel; // Last good position/time
  dd: string; // Device description - read only
  ip: string; // Last IP Address (could be null, or IP V4)
  desc: string; // Description - editable from CAS Ops
  id: number;
  mSim?: string; // the modem SIM
  mImei?: string; // the modem IMEI
  stD: DisplayStatus;
  editObj: boolean; // Whether or not the ObjectType (in the BasicModel) can be edited or not.
}

export interface RegisteredAssetModel extends RegisteredAssetBasicModel {}

export interface RegisteredAssetListFilters
  extends Partial<
    Pick<
      FiltersState,
      | 'assetGroups'
      | 'configGroups'
      | 'objectTypes'
      | 'deviceTypes'
      | 'softwareStatus'
      | 'healthStatus'
    >
  > {}

export type RegisteredAssetListQueryParams = RegisteredAssetListFilters;

export interface RegisteredAssetListQueryModel {
  query: RegisteredAssetListQueryParams;
}
export interface EditRegisteredAssetFormModel
  extends Pick<RegisteredAssetDetailModel, 'n' | 'ags' | 'cfg' | 'obj' | 'desc'> {}

export interface RegisteredAssetDeviceFileDeleteRequest extends ModelWithId {
  fileId: number;
}

export interface RegisteredAssetFormModel extends EditRegisteredAssetFormModel {}

export interface LiveAssetLocationModel extends PositionModel {
  id: number;
}

type LiveState = Record<number, LiveAssetLocationModel>;

export const registeredAssetsApi = casOpsApi.injectEndpoints({
  endpoints: build => ({
    listAssets: build.query<
      CollectionFilteredModel<RegisteredAssetBasicModel>,
      RegisteredAssetListQueryModel | void
    >({
      query: arg => ({
        url: 'api/assets',
        params: arg ? getUrlParams(arg.query) : undefined,
      }),
      providesTags: (result, error, arg) =>
        result ? [{ type: QueryTag.AssetRegistered, id: TagList }] : [],
    }),
    liveAssetLocations: build.query<LiveState, void>({
      queryFn: () => ({ data: {} }),
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const es = new EventSource('api/assetlocations');

        try {
          await cacheDataLoaded;

          const onMessage = (e: MessageEvent) => {
            try {
              const updates = JSON.parse(e.data) as LiveAssetLocationModel[];
              if (!updates.length) return;
              updateCachedData(draft => {
                for (const u of updates) {
                  draft[u.id] = u;
                }
              });
            } catch (err) {
              console.log('EventSource Error', err);
            }
          };

          es.onmessage = onMessage;

          await cacheEntryRemoved;
        } finally {
          es.close();
        }
      },
    }),
    getAsset: build.query<RegisteredAssetDetailModel, ModelWithId>({
      query: ({ id }) => `api/assets/${id}`,
      providesTags: (result, error, { id }) =>
        result ? [{ type: QueryTag.AssetRegistered, id: id }] : [],
    }),
    deleteAsset: build.mutation<void, ModelWithId>({
      query: ({ id }) => ({
        url: `api/assets/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.AssetRegistered, id: TagList },
              { type: QueryTag.AssetRegistered, id: id },
            ]
          : [],
    }),
    deleteAssetDeviceFile: build.mutation<void, RegisteredAssetDeviceFileDeleteRequest>({
      query: ({ id, fileId }) => ({
        url: `api/assets/${id}/files/${fileId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) =>
        !error ? [{ type: QueryTag.AssetRegistered, id: id }] : [],
    }),
    editAsset: build.mutation<void, EditRegisteredAssetFormModel & ModelWithId>({
      query: ({ id, ...body }) => ({
        url: `api/assets/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.AssetRegistered, id: TagList },
              { type: QueryTag.AssetRegistered, id: id },
            ]
          : [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListAssetsQuery,
  useLiveAssetLocationsQuery,
  useGetAssetQuery,
  useDeleteAssetMutation,
  useDeleteAssetDeviceFileMutation,
  useEditAssetMutation,
} = registeredAssetsApi;
